import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface Profile {
    id: string;
    email: string;
    phone: string;
    credits: number;
    is_active: boolean;
    role: string;
    created_at: string;
    // New optional fields for UI
    total_generated?: number;
    monthly_generated?: number;
}

export default function AdminDashboard() {
    const { profile } = useAuth();
    const [users, setUsers] = useState<Profile[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<Profile[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [stats, setStats] = useState({ totalUsers: 0, activeUsers: 0, totalCredits: 0 });
    const [fetchError, setFetchError] = useState<string | null>(null);

    // Filter State
    type DateFilter = 'ALL' | 'TODAY' | 'WEEK' | 'MONTH';
    const [dateFilter, setDateFilter] = useState<DateFilter>('ALL');

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;

            // Fetch usage stats
            const { data: statsData, error: statsError } = await supabase
                .rpc('get_user_stats');

            if (statsError) {
                console.error('Error fetching stats:', statsError);
            }

            const statsMap = (statsData || {}) as Record<string, { total_generated: number; monthly_generated: number }>;

            const profiles = (data || []).map(p => ({
                ...p,
                total_generated: statsMap[p.id]?.total_generated || 0,
                monthly_generated: statsMap[p.id]?.monthly_generated || 0
            }));

            setUsers(profiles);

            // Calculate stats
            const totalUsers = profiles.length;
            const activeUsers = profiles.filter(p => p.is_active).length;
            const totalCredits = profiles.reduce((sum, p) => sum + (p.credits || 0), 0);

            setStats({ totalUsers, activeUsers, totalCredits });
        } catch (err: any) {
            console.error('Error fetching users:', err);
            setFetchError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    // Filter users when search query or date filter changes
    useEffect(() => {
        if (!users) return;
        const lowerQuery = searchQuery.toLowerCase();

        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay()); // Sunday as start
        startOfWeek.setHours(0, 0, 0, 0);

        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

        const filtered = users.filter(user => {
            // Text Search
            const matchesSearch = (user.email?.toLowerCase() || '').includes(lowerQuery) ||
                (user.id || '').includes(lowerQuery) ||
                (user.phone || '').includes(lowerQuery);

            if (!matchesSearch) return false;

            // Date Filter
            const userDate = new Date(user.created_at);
            if (dateFilter === 'TODAY') {
                return userDate >= startOfToday;
            } else if (dateFilter === 'WEEK') {
                return userDate >= startOfWeek;
            } else if (dateFilter === 'MONTH') {
                return userDate >= startOfMonth;
            }
            return true;
        });

        setFilteredUsers(filtered);
    }, [searchQuery, users, dateFilter]);

    const toggleActivation = async (userId: string, currentStatus: boolean) => {
        try {
            const { error } = await supabase
                .from('profiles')
                .update({ is_active: !currentStatus })
                .eq('id', userId);

            if (error) throw error;

            // Refresh data
            fetchUsers();
        } catch (error: any) {
            console.error('Error toggling activation:', error);
            alert('Failed to update status: ' + error.message);
        }
    };

    const resetCredits = async (userId: string) => {
        if (!confirm('Are you sure you want to RESET credits to 0 for this user?')) return;
        try {
            const { error } = await supabase
                .from('profiles')
                .update({ credits: 0 })
                .eq('id', userId);

            if (error) throw error;
            fetchUsers();
            alert('Credits reset to 0.');
        } catch (error: any) {
            console.error('Error resetting credits:', error);
            alert('Failed to reset credits: ' + error.message);
        }
    };

    const addCredits = async (userId: string, amount: number) => {
        try {
            const user = users.find(u => u.id === userId);
            if (!user) return;

            const { error } = await supabase
                .from('profiles')
                .update({ credits: (user.credits || 0) + amount })
                .eq('id', userId);

            if (error) throw error;

            // Refresh data
            fetchUsers();
            alert(`Successfully added ${amount} credits.`);
        } catch (error: any) {
            console.error('Error adding credits:', error);
            alert('Failed to add credits: ' + error.message);
        }
    };

    // ... Loading UI

    return (
        <div className="p-8 bg-black min-h-full text-gray-200">
            {/* Header & KPI Cards ... */}
            {/* (Assuming existing header code is preserved by context, focusing on table update) */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white tracking-tight">Superadmin Dashboard</h1>
                    <p className="text-gray-400 mt-1">Manage users, credits, and usage stats.</p>
                </div>
                <div className="flex gap-2">
                    <Link to="/app" className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
                        <span>&larr; Back to Workspace</span>
                    </Link>
                    <button onClick={fetchUsers} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm font-medium transition-colors">
                        Refresh Data
                    </button>
                </div>
            </div>



            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                <div className="p-6 bg-gray-900/50 border border-gray-800 rounded-2xl">
                    <p className="text-gray-400 text-sm font-medium uppercase tracking-wider">Total Users</p>
                    <p className="text-4xl font-bold text-white mt-2">{stats.totalUsers}</p>
                </div>
                <div className="p-6 bg-gray-900/50 border border-gray-800 rounded-2xl">
                    <p className="text-gray-400 text-sm font-medium uppercase tracking-wider">Active Accounts</p>
                    <p className="text-4xl font-bold text-green-400 mt-2">{stats.activeUsers}</p>
                </div>
                <div className="p-6 bg-gray-900/50 border border-gray-800 rounded-2xl">
                    <p className="text-gray-400 text-sm font-medium uppercase tracking-wider">Total Credits Issued</p>
                    <p className="text-4xl font-bold text-indigo-400 mt-2">{stats.totalCredits.toLocaleString()}</p>
                </div>
            </div>

            {/* Search Bar & Filters */}
            <div className="mb-6 flex flex-col md:flex-row gap-4 justify-between items-end">
                <div className="relative max-w-md w-full">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                    <input
                        type="text"
                        className="block w-full pl-10 pr-3 py-3 border border-gray-700 rounded-xl leading-5 bg-gray-900 text-gray-300 placeholder-gray-500 focus:outline-none focus:bg-gray-800 focus:border-indigo-500 transition-colors"
                        placeholder="Search by email, phone, or ID..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                {/* Date Filters */}
                <div className="flex bg-gray-900 border border-gray-800 rounded-xl p-1">
                    {(['ALL', 'TODAY', 'WEEK', 'MONTH'] as DateFilter[]).map((filter) => (
                        <button
                            key={filter}
                            onClick={() => setDateFilter(filter)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${dateFilter === filter
                                ? 'bg-gray-700 text-white shadow-md'
                                : 'text-gray-400 hover:text-white hover:bg-gray-800'
                                }`}
                        >
                            {filter}
                        </button>
                    ))}
                </div>
            </div>

            {/* Users Table */}
            <div className="overflow-hidden bg-gray-900 border border-gray-800 rounded-2xl shadow-xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-800/50 text-gray-400 uppercase text-xs font-semibold tracking-wider">
                                <th className="p-5 border-b border-gray-700">User / Email</th>
                                <th className="p-5 border-b border-gray-700">Role & Status</th>
                                <th className="p-5 border-b border-gray-700 text-center">Lifetime Generated</th>
                                <th className="p-5 border-b border-gray-700 text-center">Monthly Generated</th>
                                <th className="p-5 border-b border-gray-700">Credits</th>
                                <th className="p-5 border-b border-gray-700 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800">
                            {fetchError ? (
                                <tr>
                                    <td colSpan={6} className="p-8 text-center text-red-400">
                                        <div className="flex flex-col items-center gap-2">
                                            <span>Start Fetch Error: {fetchError}</span>
                                            <span className="text-xs text-gray-500">Check console for details. Usually means RLS policy issue.</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredUsers.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="p-8 text-center text-gray-500">
                                        No users found matching "{searchQuery}"
                                    </td>
                                </tr>
                            ) : (
                                filteredUsers.map(user => (
                                    <tr key={user.id} className="hover:bg-gray-800/30 transition-colors">
                                        <td className="p-5">
                                            <div className="flex flex-col">
                                                <span className="text-white font-medium">{user.email}</span>
                                                <span className="text-xs text-gray-500 font-mono mt-1">{user.id}</span>
                                                {user.phone && <span className="text-xs text-gray-400 mt-0.5">📞 {user.phone}</span>}
                                            </div>
                                        </td>
                                        <td className="p-5">
                                            <div className="flex gap-2">
                                                <span className={`px-2 py-1 rounded-md text-xs font-bold border ${user.role === 'superadmin' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : 'bg-gray-700/30 text-gray-400 border-gray-600/30'}`}>
                                                    {user.role}
                                                </span>
                                                <span className={`px-2 py-1 rounded-md text-xs font-bold border ${user.is_active ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>
                                                    {user.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="p-5 text-center">
                                            <span className="text-indigo-300 font-mono font-bold">
                                                {user.total_generated?.toLocaleString() || 0}
                                            </span>
                                        </td>
                                        <td className="p-5 text-center">
                                            <span className="text-blue-300 font-mono font-bold">
                                                {user.monthly_generated?.toLocaleString() || 0}
                                            </span>
                                        </td>
                                        <td className="p-5">
                                            <div className="font-mono text-xl font-bold text-white">
                                                {user.credits.toLocaleString()}
                                            </div>
                                        </td>
                                        <td className="p-5 text-right">
                                            <div className="flex items-center justify-end gap-3">
                                                <button
                                                    onClick={() => toggleActivation(user.id, user.is_active)}
                                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${user.is_active ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' : 'bg-green-500/10 text-green-400 hover:bg-green-500/20'}`}
                                                >
                                                    {user.is_active ? 'Deactivate' : 'Activate'}
                                                </button>

                                                <button
                                                    onClick={() => resetCredits(user.id)}
                                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-800 hover:bg-red-900/30 text-gray-400 hover:text-red-400 border border-gray-700 transition-all hover:border-red-800"
                                                    title="Reset balance to 0"
                                                >
                                                    Reset
                                                </button>

                                                <div className="h-6 w-px bg-gray-700 mx-1"></div>

                                                <button
                                                    onClick={() => addCredits(user.id, 10)}
                                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-800 hover:bg-gray-700 text-white border border-gray-700 transition-all hover:border-gray-600"
                                                >
                                                    +10
                                                </button>
                                                <button
                                                    onClick={() => addCredits(user.id, 100)}
                                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-500/20 transition-all hover:-translate-y-0.5"
                                                >
                                                    +100
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

